import { crmDel, crmGet, crmPost, crmPut } from "../api/api";
import {
  getAccessToken,
  getTenantId,
  getTenantCode,
} from "../utils/sessionDB";
import { getData, getMessage } from "../utils/responseDecoder";

const CRM08_READ = "CRM08S";
const CRM08_ADD = "CRM08A";
const CRM08_DELETE = "CRM08D";
const CRM09_READ = "CRM09S";
const CRM09_UPDATE = "CRM09U";
const CRM10_READ = "CRM10S";
const CRM10_ADD = "CRM10A";
const CRM10_UPDATE = "CRM10U";
const CRM10_DELETE = "CRM10D";
const CRM11_READ = "CRM11S";
const CRM11_ADD = "CRM11A";
const CRM11_UPDATE = "CRM11U";
const CRM11_DELETE = "CRM11D";

function decodePayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.encrypted === true || payload.encrypted === "true") {
    const decoded = getData(JSON.stringify(payload));
    return decoded ?? payload.data ?? payload;
  }
  return payload.data !== undefined ? payload.data : payload;
}

function normalizeResponse(payload) {
  return {
    raw: payload,
    data: decodePayload(payload),
    message: payload.message || "",
  };
}

async function request(method, path, { serviceCode, body } = {}) {
  try {
    let payload;

    switch (method) {
      case "GET":
        payload = await crmGet(path.replace("/saas/crm", ""), serviceCode);
        break;
      case "POST":
        payload = await crmPost(path.replace("/saas/crm", ""), body ?? {}, serviceCode);
        break;
      case "PUT":
        payload = await crmPut(path.replace("/saas/crm", ""), body ?? {}, serviceCode);
        break;
      case "DELETE":
        payload = await crmDel(path.replace("/saas/crm", ""), serviceCode);
        break;
      default:
        throw new Error(`Unsupported request method: ${method}`);
    }

    return normalizeResponse(payload);
  } catch (error) {
    const message =
      error?.message ||
      (typeof error?.data === "string" ? getMessage(error.data) : "") ||
      "Request failed";
    const normalizedError = new Error(message);
    normalizedError.status = error?.status;
    normalizedError.payload = error?.data ?? error?.payload ?? null;
    throw normalizedError;
  }
}

export function hasLiveSalesConfigSession() {
  const token = String(getAccessToken() || "");
  const tenantId = String(getTenantId() || "").trim();
  const tenantCode = String(getTenantCode() || "").trim();
  const normalized = token.replace(/^Bearer\s+/i, "").trim();
  const looksLikeJwt = normalized.split(".").length >= 3;

  return Boolean(normalized) && looksLikeJwt && Boolean(tenantId) && Boolean(tenantCode);
}

export async function getApprovalRules() {
  return request("GET", "/saas/crm/approval-rules/get", {
    serviceCode: CRM08_READ,
  });
}

export async function createApprovalRule(payload) {
  return request("POST", "/saas/crm/approval-rules/add", {
    serviceCode: CRM08_ADD,
    body: payload,
  });
}

export async function deleteApprovalRule(id) {
  return request("DELETE", `/saas/crm/approval-rules/delete/${id}`, {
    serviceCode: CRM08_DELETE,
  });
}

export async function getNetPriceRule() {
  return request("GET", "/saas/crm/net-price-rules/get", {
    serviceCode: CRM09_READ,
  });
}

export async function updateNetPriceRule(payload) {
  return request("PUT", "/saas/crm/net-price-rules/update", {
    serviceCode: CRM09_UPDATE,
    body: payload,
  });
}

export async function getPmcConfigs(quotationType = "") {
  const params = new URLSearchParams();
  const type = String(quotationType || "").trim();
  if (type) {
    params.set("quotationType", type);
  }

  return request("GET", `/saas/crm/pmc-configs/get${params.toString() ? `?${params.toString()}` : ""}`, {
    serviceCode: CRM10_READ,
  });
}

export async function createPmcConfig(payload) {
  return request("POST", "/saas/crm/pmc-configs/add", {
    serviceCode: CRM10_ADD,
    body: payload,
  });
}

export async function updatePmcConfig(id, payload) {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error("Invalid PMC config id.");
  }

  const path = `/saas/crm/pmc-configs/update/${normalizedId}`;

  try {
    return await request("PUT", path, {
      serviceCode: CRM10_UPDATE,
      body: payload,
    });
  } catch (error) {
    if (!isCorsOrMethodBlocked(error)) throw error;

    // Gateway CORS often blocks PUT preflight (OPTIONS 403), same as DELETE.
    return request("POST", path, {
      serviceCode: CRM10_UPDATE,
      body: payload,
    });
  }
}

export async function createPmcConfigLine(payload) {
  return request("POST", "/saas/crm/pmc-configs/lines/add", {
    serviceCode: CRM10_ADD,
    body: payload,
  });
}

export async function updatePmcConfigLine(pmcConfigId, payload) {
  const configId = Number(pmcConfigId);
  if (!Number.isFinite(configId) || configId <= 0) {
    throw new Error("Invalid PMC cost head id.");
  }

  const path = `/saas/crm/pmc-configs/lines/update/${configId}`;

  try {
    return await request("PUT", path, {
      serviceCode: CRM10_UPDATE,
      body: payload,
    });
  } catch (error) {
    if (!isCorsOrMethodBlocked(error)) throw error;

    return request("POST", path, {
      serviceCode: CRM10_UPDATE,
      body: payload,
    });
  }
}

function isCorsOrMethodBlocked(error) {
  const status = Number(error?.status) || 0;
  const message = String(error?.message || "").toLowerCase();
  return (
    !status ||
    status === 403 ||
    status === 405 ||
    message.includes("failed to fetch") ||
    message.includes("network") ||
    message.includes("cors")
  );
}

export async function deletePmcConfig(id) {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error("Invalid PMC config id.");
  }

  const path = `/saas/crm/pmc-configs/delete/${normalizedId}`;

  try {
    return await request("DELETE", path, {
      serviceCode: CRM10_DELETE,
    });
  } catch (error) {
    if (!isCorsOrMethodBlocked(error)) throw error;

    // Gateway CORS often blocks DELETE preflight (OPTIONS 403). The same path
    // accepts POST with the delete service code when that happens.
    return request("POST", path, {
      serviceCode: CRM10_DELETE,
      body: {},
    });
  }
}

export async function getTerms(type) {
  const apiType = mapTermsTypeForApi(type);
  if (!apiType) {
    throw new Error("Quotation type is required to load terms.");
  }

  return request("GET", `/saas/crm/terms/get/${encodeURIComponent(apiType)}`, {
    serviceCode: CRM11_READ,
  });
}

/** Terms Library: load terms across quotation types and merge by id. */
export async function getAllTerms() {
  const types = ["SUPPLY", "SITC", "SERVICE"];
  const results = await Promise.allSettled(types.map((type) => getTerms(type)));

  const byId = new Map();
  results.forEach((result) => {
    if (result.status !== "fulfilled") return;
    const payload = result.value?.data ?? result.value;
    const collection = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.content)
        ? payload.content
        : Array.isArray(payload?.terms)
          ? payload.terms
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.content)
              ? payload.data.content
              : payload && typeof payload === "object" && payload.id != null
                ? [payload]
                : [];

    collection.forEach((term) => {
      const id = term?.id ?? term?.termId ?? term?.term_id;
      if (id == null) return;
      const key = String(id);
      const existing = byId.get(key);
      byId.set(key, existing ? { ...existing, ...term } : term);
    });
  });

  const fulfilled = results.filter((result) => result.status === "fulfilled");
  if (!fulfilled.length) {
    const firstRejected = results.find((result) => result.status === "rejected");
    throw firstRejected?.reason || new Error("Failed to load terms.");
  }

  return {
    data: Array.from(byId.values()),
  };
}

function mapTermsTypeForApi(type) {
  const normalized = String(type || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (!normalized) return "";
  if (normalized.includes("INSTALL") || normalized === "SITC") return "SITC";
  if (normalized === "SERVICE") return "SERVICE";
  if (normalized === "SUPPLY") return "SUPPLY";
  // UI label "Supply" and other supply-like values map to SUPPLY.
  return "SUPPLY";
}

export async function createTerm(payload) {
  return request("POST", "/saas/crm/terms/add", {
    serviceCode: CRM11_ADD,
    body: payload,
  });
}

export async function updateTerm(id, payload) {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error("Invalid term id.");
  }

  return request("PUT", `/saas/crm/terms/update/${normalizedId}`, {
    serviceCode: CRM11_UPDATE,
    body: payload,
  });
}

export async function deleteTerm(id) {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error("Invalid term id.");
  }

  return request("DELETE", `/saas/crm/terms/delete/${normalizedId}`, {
    serviceCode: CRM11_DELETE,
  });
}
